import React, { useEffect } from "react";
import styles from "./ButtonStateTabset.module.css";
import AppConstants from "../../constants/AppConstants";

const { BUTTON_STATE_REGULAR, BUTTON_STATE_HOVER } = AppConstants;

const DEFAULT_OPTIONS = [
  { value: BUTTON_STATE_REGULAR, label: "Normal" },
  { value: BUTTON_STATE_HOVER, label: "Hover" },
];

/**
 * ButtonStateTabset component for toggling between normal and hover states
 * @param {Object} props
 * @param {string} props.buttonState - Current button state (BUTTON_STATE_REGULAR or BUTTON_STATE_HOVER)
 * @param {function} props.setButtonState - Function to update the button state
 * @param {string=} props.buttonType - Optional button type (e.g., primary/secondary)
 * @param {string=} props.slot - Optional slot name for web component usage
 * @param {{ value: string, label: string }[]=} props.options - Tabs, in order
 * @param {string=} props.resetState - State restored on unmount
 * @returns {JSX.Element} A tabset UI component for switching between button states
 */
const ButtonStateTabset = ({
  buttonState,
  setButtonState,
  buttonType,
  slot,
  options = DEFAULT_OPTIONS,
  resetState = BUTTON_STATE_REGULAR,
  fullWidth = false,
  testId,
}) => {
  const handleClick = (newState) => {
    if (buttonType) {
      setButtonState(buttonType, newState);
    } else {
      setButtonState(newState);
    }
  };

  // Reset the button state when the component unmounts to avoid confusion when state select not visible
  useEffect(() => () => setButtonState(resetState), []);

  return (
    <div className={fullWidth ? `${styles.root} ${styles.fullWidth}` : styles.root} slot={slot}>
      {options.map((option, index) => (
        <React.Fragment key={option.value}>
          {index > 0 && <div className={styles.divider} />}
          <button
            type="button"
            className={styles.tab}
            data-active={buttonState === option.value}
            data-e2e={testId && `${testId}-${option.value}`}
            data-testid={testId && `${testId}-${option.value}`}
            onClick={() => handleClick(option.value)}
          >
            {option.label}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
};

export default ButtonStateTabset;
